import React, { useMemo, useState, useEffect } from 'react';
import { TableCell, Typography, Box, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  StyledTableRow,
  iconSiteBox,
  noLiveDataTableCell,
  iconAlarmBox,
  StyledTableDirectionsCell,
  StyledTableSiteCell,
  StyledTableAlarmCell,
  StyledTableSiteCellBox,
} from './style';
import { NewSite } from 'src/features/sites/types/sites';
import { useSite } from 'src/contexts/siteContext/sites.context';
import { SiteInterface } from 'src/features/sites/types/site';
import { useNavigate } from 'react-router';
import { SITES_DASHBOARD_PATH, SITES_MANAGEMENT_PATH } from 'src/routes/config';
import { useAuthentication } from 'src/features/authentication/context';
import { WarningIcon, InfoIcon } from 'src/features/theme/overrides/CustomIcons';
import { AlarmPriorityType } from 'src/features/alarm-rules/types';
import { GetDirectionsButton } from '../../get-directions-button/GetDirectionsButton';
import { NoLiveDataSiteCard } from '../../no-live-data-site-card';
import { SiteTypeIcon } from '../../site-type-icon';
import { TagsList } from '../../tags-list';
import { TagsTypes } from 'src/features/sites/types/types';
import { SitesService } from 'src/services/sites/sites.service';
import { OfflineIcon } from 'src/assets/icons/OfflineIcon';
import { AlarmWarningIcon } from 'src/assets/icons/AlarmWarningIcon';
import { SpannerIcon } from 'src/assets/icons/SpannerIcon';

type TableRowItemTypes = {
  row?: NewSite;
  selected?: boolean;
  testId?: string;
  onSelectRow: VoidFunction;
  tags: TagsTypes;
  liveData?: any;
  handleEditSitePreview: (siteId: string | null) => void;
  roleAccessInfoBox: boolean;
};

function areEqual(prevProps: TableRowItemTypes, nextProps: TableRowItemTypes) {
  if (prevProps.row?.id !== nextProps.row?.id) return false;
  if (prevProps.row?.isMaintenance !== nextProps.row?.isMaintenance) return false;
  if (prevProps.selected !== nextProps.selected) return false;

  const prevTags = prevProps.tags?.tags || [];
  const nextTags = nextProps.tags?.tags || [];

  if (prevTags.length !== nextTags.length) return false;
  if (prevProps.roleAccessInfoBox !== nextProps.roleAccessInfoBox) return false;

  const prevLive = prevProps.liveData?.data || null;
  const nextLive = nextProps.liveData?.data || null;

  if (!prevLive && !nextLive) return true;
  if (!prevLive !== !nextLive) return false;
  if (prevLive.siteId !== nextLive.siteId) return false;
  if (JSON.stringify(prevLive.data) !== JSON.stringify(nextLive.data)) return false;

  return true;
}

export const TableRowItem = React.memo(
  ({
    row,
    selected,
    onSelectRow,
    tags,
    handleEditSitePreview,
    liveData,
    roleAccessInfoBox,
  }: TableRowItemTypes) => {
    const { saveSite } = useSite();
    const {
      customerId: { value: customerId },
    } = useAuthentication();
    const navigate = useNavigate();
    const theme = useTheme();

    const [liveDataMap, setLiveDataMap] = useState<Record<string, any>>({});

    const noDataAndShowDataTrue = !tags?.tags?.length && tags?.showLiveData === true;
    const displayNoLiveDataLabel = !tags || noDataAndShowDataTrue;

    const [noLiveDataLabel, setNoLiveDataLabel] = useState<boolean>(displayNoLiveDataLabel);

    useEffect(() => {
      setNoLiveDataLabel(displayNoLiveDataLabel);
    }, [displayNoLiveDataLabel]);

    useEffect(() => {
      if (!liveData?.data) return;

      const gatewayId = liveData.data.sourceId;
      setLiveDataMap((prev) => ({
        ...prev,
        [gatewayId]: liveData,
      }));
    }, [liveData]);

    const combinedTags = useMemo(
      () => Object.values(liveDataMap).flatMap((msg: any) => msg.data?.data || []),
      [liveDataMap]
    );

    const liveDataComplete = {
      siteId: row?.id,
      tags: combinedTags,
    };

    const removeNoLiveDataLabelHandler = () => {
      setNoLiveDataLabel(false);
    };

    const lengtOfConnectedTags = tags?.tags?.length;

    const handleRedirect = (siteId: string) => {
      if (!customerId) return;
      navigate(SITES_DASHBOARD_PATH + `?customerId=${customerId}&siteId=${siteId}`);
    };

    const handleRowClick = () => {
      if (!customerId) return;
      SitesService.getById({ customerId: customerId, siteId: row!.id })
        .then((data: SiteInterface) => {
          saveSite(data);
        })
        .catch((error) => console.error(error));
      handleRedirect(row!.id);
      onSelectRow();
    };

    const handleAlarmView = (priority: AlarmPriorityType) => {
      switch (priority) {
        case 'Critical':
          return <AlarmWarningIcon fill={theme.palette.error.main} />;
        case 'Warning':
          return <WarningIcon htmlColor={theme.palette.warning.main} />;
        case 'Info':
          return <InfoIcon htmlColor={theme.palette.secondary.main} />;
        case 'Offline':
          return <OfflineIcon fill={theme.palette.error.main} width={20} height={20} />;
        default:
          return '-';
      }
    };

    const onMiddleMouseClick = (event: any) => {
      event.preventDefault();
      if (event.button === 1) {
        const currentLocation = window.location.href;
        let newLocation = '';
        if (currentLocation.includes('siteId')) {
          const locationSplit = currentLocation.split('siteId');
          newLocation =
            locationSplit[0].replace(SITES_MANAGEMENT_PATH, SITES_DASHBOARD_PATH) +
            `siteId=${row?.id}`;
        } else {
          newLocation =
            currentLocation.replace(SITES_MANAGEMENT_PATH, SITES_DASHBOARD_PATH) +
            `&siteId=${row?.id}`;
        }

        window.open(newLocation, '_blank', 'noopener');
        window.blur();
      }
    };

    const isTopPositionCell = lengtOfConnectedTags > 4;
    const siteName = row?.siteName;

    return (
      <>
        <StyledTableRow
          theme={theme}
          hover
          selected={selected}
          data-sm="table-row"
          onMouseDown={onMiddleMouseClick}
          onClick={!lengtOfConnectedTags ? handleRowClick : () => {}}
          lengtOfConnectedTags={!!lengtOfConnectedTags}
        >
          <StyledTableSiteCell
            data-sm="site-name-row"
            isTopPosition={isTopPositionCell}
            onClick={handleRowClick}
          >
            <StyledTableSiteCellBox isTags={!!lengtOfConnectedTags}>
              <Box sx={iconSiteBox}>
                <SiteTypeIcon siteType={row?.siteType!!} />
              </Box>
              {lengtOfConnectedTags && siteName && siteName.length > 55 ? (
                <Tooltip title={siteName}>
                  <Typography sx={{ textAlign: 'left' }} variant="subtitle2">
                    {siteName?.substring(0, 55)} ...
                  </Typography>
                </Tooltip>
              ) : (
                <Typography sx={{ textAlign: 'left' }} variant="subtitle2">
                  {siteName}
                </Typography>
              )}
            </StyledTableSiteCellBox>
          </StyledTableSiteCell>
          {noLiveDataLabel && roleAccessInfoBox ? (
            <TableCell sx={noLiveDataTableCell}>
              <NoLiveDataSiteCard
                testId="no-live-data-site-card"
                handleEditSitePreview={handleEditSitePreview}
                siteId={row?.id ?? null}
                customerId={customerId}
                removeNoLiveDataLabelHandler={removeNoLiveDataLabelHandler}
              />
            </TableCell>
          ) : (
            <TableCell>
              <TagsList
                isMobile={false}
                tags={tags}
                testId="tags-list"
                liveDataComplete={liveDataComplete}
              />
            </TableCell>
          )}
          <StyledTableAlarmCell
            data-sm="open-issues-row"
            isTopPosition={row?.isMaintenance ? false : isTopPositionCell}
          >
            <Box sx={{ ...iconAlarmBox, alignItems: 'center' }}>
              {row?.isMaintenance ? (
                <Box
                  data-sm="maintenance-mode-icon"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    backgroundColor: theme.palette.action.selected,
                    width: 24,
                    height: 24,
                  }}
                >
                  <SpannerIcon fill={theme.palette.text.secondary} width={14} height={14} />
                </Box>
              ) : (
                handleAlarmView(row?.highestActiveAlarm!)
              )}
            </Box>
          </StyledTableAlarmCell>
          <StyledTableDirectionsCell isTopPosition={row?.isMaintenance ? false : isTopPositionCell}>
            <GetDirectionsButton row={row} handleEditSitePreview={handleEditSitePreview} />
          </StyledTableDirectionsCell>
        </StyledTableRow>
      </>
    );
  },
  areEqual
);
